/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import { Eye, Edit, Check, X } from 'lucide-react';
import { ThumbnailCell } from './ThumbnailCell';
import LoadingDots from '../loadingdots/LoadingDots';
import { Column } from '.';
import { getNestedValue } from 'utils/Functions';

interface TableRowProps<T> {
  row: T;
  columns: Column[];
  rowIndex: number;
  editingRow: number | null;
  editedRowData: Partial<T> | null;
  isLoading?: boolean;
  onRowClick?: (row: T) => void;
  handleEditStart: (rowIndex: number) => void;
  handleEditCancel: () => void;
  handleEditSave: () => void;
  handleFieldChange: (key: string, value: any) => void;
  isProcessor: boolean;
  isOfficer: boolean;
  onSend?: (row: T) => void;
  onSendingSms?: boolean;
  selectable?: boolean;
  isSelected?: boolean;
  onToggleSelect?: (row: T) => void;
}

const DefaultEditInput = ({
  value,
  onChange,
  onBlur,
}: {
  value: any;
  onChange: (value: any) => void;
  onBlur?: () => void;
}) => (
  <input
    type="text"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    onBlur={onBlur}
    className="w-full px-2 py-1 border rounded"
  />
);

const SmsConfirmModal = ({
  onConfirm,
  onCancel,
}: {
  onConfirm: () => void;
  onCancel: () => void;
}) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center">
    {/* Backdrop */}
    <button
      type="button"
      aria-label="Close send SMS confirmation"
      className="absolute inset-0 bg-black/40"
      onClick={onCancel}
    />
    {/* Dialog */}
    <div className="relative bg-white rounded-xl shadow-xl p-6 w-80 flex flex-col gap-4">
      <button
        onClick={onCancel}
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 p-1 rounded"
      >
        <X size={16} />
      </button>
      <div className="flex flex-col gap-1">
        <h2 className="text-base font-semibold text-gray-800">Send SMS?</h2>
        <p className="text-sm text-gray-500">
          Are you sure you want to send an SMS to this recipient? This action
          cannot be undone.
        </p>
      </div>
      <div className="flex justify-end gap-2">
        <button
          onClick={onCancel}
          className="px-4 py-2 rounded-md text-sm font-medium text-gray-600 border border-gray-200 hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          className="px-4 py-2 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors"
        >
          Send SMS
        </button>
      </div>
    </div>
  </div>
);

export const TableRow = <T extends object>({
  row,
  columns,
  rowIndex,
  editingRow,
  editedRowData,
  isLoading,
  onRowClick,
  handleEditStart,
  handleEditCancel,
  handleEditSave,
  handleFieldChange,
  isProcessor,
  isOfficer,
  onSend,
  onSendingSms,
  selectable = false,
  isSelected = false,
  onToggleSelect,
}: TableRowProps<T>) => {
  const [showSmsModal, setShowSmsModal] = useState(false);

  const handleSendClick = () => setShowSmsModal(true);
  const handleConfirm = () => {
    setShowSmsModal(false);
    onSend?.(row);
  };
  const handleCancel = () => setShowSmsModal(false);

  return (
    <>
      {showSmsModal && (
        <SmsConfirmModal onConfirm={handleConfirm} onCancel={handleCancel} />
      )}
      <tr
        key={rowIndex}
        className={`${onRowClick ? 'cursor-pointer hover:bg-gray-50' : ''} transition-colors}`}
      >
        {selectable && (
          <td className="px-4 sm:px-5 py-2.5 text-sm text-gray-600 whitespace-nowrap">
            <input
              type="checkbox"
              aria-label={`Select row ${rowIndex + 1}`}
              checked={isSelected}
              onChange={() => onToggleSelect?.(row)}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
          </td>
        )}
        {columns.map((column, colIndex) => {
          const cellValue = getNestedValue(row, column.accessorKey);

          if (column.thumbnail) {
            return (
              <td
                key={colIndex}
                className="px-4 sm:px-5 py-2.5 text-sm text-gray-600 whitespace-nowrap"
              >
                <ThumbnailCell
                  src={cellValue}
                  thumbnailConfig={column.thumbnail}
                />
              </td>
            );
          }

          if (editingRow === rowIndex && column.editable) {
            const EditComponent = column.editComponent || DefaultEditInput;
            return (
              <td
                key={colIndex}
                className="px-4 sm:px-5 py-2.5 text-sm text-gray-600 whitespace-nowrap"
              >
                <EditComponent
                  value={
                    editedRowData?.[
                      column.accessorKey as keyof typeof editedRowData
                    ]
                  }
                  onChange={(value) =>
                    handleFieldChange(
                      column.accessorKey as keyof typeof editedRowData,
                      value,
                    )
                  }
                />
              </td>
            );
          }

          return (
            <td
              key={colIndex}
              className="px-4 sm:px-5 py-2.5 text-sm text-gray-600 whitespace-nowrap"
            >
              {column.cell ? column.cell(cellValue) : cellValue}
            </td>
          );
        })}

        <td className="px-4 sm:px-6 py-4 text-sm text-gray-600 whitespace-nowrap">
          {isLoading ? (
            <LoadingDots className="justify-center" />
          ) : editingRow === rowIndex ? (
            <div className="flex space-x-2">
              <button
                onClick={handleEditSave}
                className="text-green-600 hover:bg-green-50 p-1 rounded"
              >
                <Check size={20} />
              </button>
              <button
                onClick={handleEditCancel}
                className="text-red-600 hover:bg-red-50 p-1 rounded"
              >
                <X size={20} />
              </button>
            </div>
          ) : (
            <div className="flex space-x-4">
              <button
                onClick={() => onRowClick?.(row)}
                className="text-blue-600 hover:bg-blue-50 p-1 rounded"
              >
                <Eye size={20} />
              </button>
              {isProcessor && (
                <button
                  onClick={() => handleEditStart(rowIndex)}
                  className="text-blue-600 hover:bg-blue-50 p-1 rounded"
                >
                  <Edit size={20} />
                </button>
              )}
              {isOfficer && (
                <button
                  disabled={onSendingSms}
                  onClick={handleSendClick}
                  className={`
                    inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium
                    transition-all duration-150 border
                    ${
                      onSendingSms
                        ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed'
                        : 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 hover:border-blue-300 active:scale-95'
                    }
                  `}
                >
                  {onSendingSms ? (
                    <>
                      <svg
                        className="animate-spin h-3 w-3 text-gray-400"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        />
                      </svg>
                      Sending…
                    </>
                  ) : (
                    <>Send SMS</>
                  )}
                </button>
              )}
            </div>
          )}
        </td>
      </tr>
    </>
  );
};
